// Onboarding i18n namespace (Module 02 §4). The shared key scaffold every S2–S9
// screen builder extends. It is wired into apps/web's aggregated `dictionaries`
// (src/i18n-dictionaries.ts) so the CI key-parity gate (`pnpm run i18n:parity`,
// TESTING §12.1 / D-014) covers it automatically.
//
// `onboardingEn` is authoritative; `OnboardingDict` widens its values to
// `string` (src/i18n-shape.ts) so every locale stays parity-checked at COMPILE time
// (a missing/extra key fails tsc) while carrying real translations.
//
// ru IS TRANSLATED — onboarding.ru.ts plus a `*.ru.ts` beside each screen
// namespace. fr, zh, zh-Hant and hi each have one onboarding.<locale>.ts for
// the whole namespace. Declared in src/i18n-completeness.ts, enforced by
// tools/ci/i18n-parity.ts.
//
// A key you add to `onboardingEn` no longer flows to all four locales for free:
// tsc will demand it in onboarding.ru.ts, and the CI gate will demand that its
// Russian value actually differs from the English one. That is the point.
//
// TWO RULES for screen builders adding keys here:
//  1. No hardcoded user-facing string anywhere in the area — every visible word
//     comes from this file (the i18n rule).
//  2. Entity NOUNS (work order, work center, stock unit, …) are NOT written into
//     screen copy. They resolve at runtime through `useTerm()` so the tenant's
//     own vocabulary wins (§2.5). The `terms` block below is only the base-label
//     fallback for the moment before `GET /v1/terminology` answers.

import type { Translated } from "../../i18n-shape.js";
import { onboardingRuDict } from "./onboarding.ru.js";
import { catalogueImportEn } from "./screens/catalogue-import.i18n.js";
import { checklistScreenEn } from "./screens/checklist/checklist.i18n.js";
import { plantMapEn } from "./screens/plant-map/plant-map.i18n.js";
import { terminologySettingsEn } from "./screens/terminology/terminology.i18n.js";
import { verticalEn } from "./screens/vertical/vertical.i18n.js";
import { packUpdateEn } from "./screens/pack-update/pack-update.i18n.js";

export const onboardingEn = {
  // Screen namespaces that live beside the screen that owns them, merged here so
  // the parity gate sees one `onboarding.*` tree: S3 (step 2 · catalogue) + S4
  // (import detail) contribute `catalogueStep.*`, `importDetail.*` and the
  // `importShared.*` vocabulary they both use.
  ...catalogueImportEn,

  // S2 (step 1 · vertical picker) and S9 (terminology settings).
  vertical: verticalEn,
  terminology: terminologySettingsEn,
  // Settings · Industry pack (D-343): the previewed pack update.
  packUpdate: packUpdateEn,

  // S5 (step 3 · plant map). Its keys live with the screen that owns them.
  plant: plantMapEn,

  // ---- shell chrome (S1) ---------------------------------------------------
  shell: {
    title: "Set up your factory",
    subtitle: "Four steps from an empty workspace to a running factory.",
    stepRailLabel: "Onboarding steps",
    targetGoLive: "Target go-live",
    targetGoLiveUnset: "Not set",
    liveBadge: "Live",
    liveSince: "Live since {{date}}",
    frozenNotice:
      "Your factory is live. Set-up is kept as a record; make changes where you do the work.",
    targetGoLiveSet: "Set a date",
    targetGoLiveChange: "Change",
    targetGoLiveFailed: "That date could not be saved. Try again.",
    forbiddenTitle: "You do not have access to setup",
    forbiddenBody:
      "Setting the factory up is done by your group or facility admin. Ask them if you need something changed.",
    loading: "Loading your onboarding session…",
    errorTitle: "Could not load the onboarding session",
    errorDetail: "Set-up could not be loaded. Try again.",
    retry: "Retry",
    requestId: "Request ID",
  },

  // Session status pills (onboarding_sessions.status, §2.1 / §3.1).
  sessionStatus: {
    draft: "Not started",
    in_progress: "In progress",
    ready_for_review: "Ready for review",
    live: "Live",
  },

  // Step-state pills (onboarding_step_states.status, §2.2).
  stepStatus: {
    not_started: "Not started",
    in_progress: "In progress",
    completed: "Completed",
  },

  // ---- the four steps (§3.1–§3.5) -----------------------------------------
  steps: {
    label: "Step {{number}}",
    of: "Step {{number}} of {{total}}",
    vertical: {
      title: "Choose your industry pack",
      blurb: "Installs the steps, units, quality checks and vocabulary for your industry.",
    },
    catalogue: {
      title: "Load your catalogue",
      blurb: "Products, materials, BOMs and routings, by hand or from a spreadsheet.",
    },
    plant: {
      title: "Map your plant",
      blurb:
        "Facilities, departments, work centers, storage locations and the people who run them.",
    },
    opening: {
      title: "Enter your opening state",
      blurb: "Opening stock, open orders, customers and suppliers.",
    },
    quality: {
      title: "Review your quality checks",
      blurb: "Confirm the checks your pack installed, or set your own ranges.",
    },
    // Kept for any future step whose screen is not built. All four steps are
    // routed today — a step with no route cannot be completed, and step 4's
    // completion is what `ready_for_review` (and therefore go-live) needs.
    unavailable: "Not available",
    unavailableHint:
      "This step opens once the parts of Assessly it needs are switched on. Nothing you have saved is lost.",
    openStep: "Open",
  },
  qualityReview: {
    heading: "Quality checks",
    intro: "Review each check, write your factory's values, then confirm.",
    stageCount: "Checks: {{count}}",
    pager: "Pages of {{stage}}",
    pageRange: "{{from}}–{{to}} of {{total}}",
    prev: "Previous",
    next: "Next",
    stage: {
      receiving: "Receiving",
      in_process: "In process",
      final: "Final",
    },
    provenance: {
      pack: "Pack v{{version}}",
      custom: "Yours",
    },
    sampling: {
      every_unit: "Every unit",
      fixed_count: "Fixed count · {{n}}",
      percentage: "{{pct}}% sample",
      first_n: "First {{n}}",
      aql: "AQL {{level}}/{{value}}",
    },
    badges: {
      blocking: "Blocking",
      autoHold: "Auto-hold on fail",
      inactive: "Disabled",
    },
    band: {
      min: "Min",
      target: "Target",
      max: "Max",
      edit: "Edit band",
      save: "Save band",
      cancel: "Cancel",
      invalid: "Min must not exceed max, and every value must be a number.",
    },
    ownership: {
      title: "Make this check yours?",
      body: "Saving your own values makes this check your factory's: pack updates will no longer change it. Inspections already opened keep the version they started with.",
      confirm: "Save my values",
    },
    deactivate: {
      action: "Disable",
      title: "Disable this check?",
      body: "Inspections already opened keep their pinned version, and a re-installed pack never re-enables a check you disabled — this choice is yours and it sticks.",
      confirm: "Disable check",
    },
    readOnly:
      "You can review and confirm, but editing checks needs the quality catalog permission — ask a group admin.",
    forbiddenTitle: "No access to quality checks",
    forbiddenBody:
      "Viewing the installed quality checks needs the quality catalog read permission — ask a group admin to grant it or to review this step.",
    empty: {
      title: "This pack installs no quality checks",
      body: "Confirm the step to record that you reviewed it — you can add your own checks later in Quality.",
    },
    loadError: "The quality checks could not be loaded.",
  },

  // ---- S1 body -------------------------------------------------------------
  home: {
    resumeTitle: "Pick up where you left off",
    resumeCta: "Resume step {{number}}",
    resumeChecklistCta: "Review the go-live checklist",
    allStepsDone: "All four steps are complete. Review the checklist, then go live.",
    stepsHeading: "Your steps",
    emptyTitle: "Let's set up your factory",
    emptyBody: "Start by choosing the industry pack that matches what you make.",
    emptyCta: "Choose an industry pack",
    scoreHeading: "Completeness",
    checklistLink: "Open the go-live checklist",
    settingsLink: "Terminology settings",
    homeLink: "Setup overview",
  },

  // ---- completeness dial ---------------------------------------------------
  score: {
    label: "Completeness score",
    value: "{{score}} of 100",
    threshold: "{{threshold}} needed to go live",
    // No i18next plural suffixes here on purpose: ru/fr/zh have different plural
    // categories from en, and the four dictionaries share one object today
    // (D-030), so a `_one/_other` pair would silently miss under `ru`. A count
    // placeholder is locale-safe; real plural forms land with the translations.
    blockingUnmet: "Blocking items outstanding: {{count}}",
    blockingClear: "No blocking items outstanding",
    neverEvaluated: "Not evaluated yet",
  },

  // ---- go-live (§3.6) ------------------------------------------------------
  goLive: {
    cta: "Go live",
    working: "Going live…",
    confirmTitle: "Take your factory live?",
    confirmBody:
      "Going live ends set-up: these steps are kept as a record and the factory starts running.",
    confirm: "Yes, go live",
    cancel: "Cancel",
    doneTitle: "You are live",
    doneBody: "Onboarding finished with a completeness score of {{score}}.",
    blockedTitle: "Not ready to go live yet",
    blockedSteps: "These steps are not complete yet: {{steps}}.",
    blockedItems: "These blocking checklist items are not passed or waived:",
    blockedScore: "Completeness is {{score}}; {{required}} is needed ({{gap}} to go).",
    blockedPermission: "Only a group admin can take the factory live.",
    alreadyLive: "Your factory is already live.",
    busy: "Another setup change is in progress. Try again in a moment.",
    failedTitle: "Go-live was refused",
  },

  // ---- shared save / continue bar -----------------------------------------
  save: {
    save: "Save",
    saving: "Saving…",
    saved: "Saved",
    saveAndContinue: "Save and continue",
    completeStep: "Mark step complete",
    completing: "Completing…",
    completed: "Step completed",
    unsaved: "You have unsaved changes.",
    leaveTitle: "Leave without saving?",
    leaveBody:
      "You have changes on this step that have not been saved. If you leave now they are lost.",
    leaveDiscard: "Leave without saving",
    leaveStay: "Stay on this step",
    readOnly: "You do not have permission to change this step.",
    failedTitle: "Could not save this step",
    failedDetail: "Your changes were not saved. Try again.",
  },

  // ---- concurrency + precondition failures (§9.1) --------------------------
  conflict: {
    staleTitle: "Someone else changed this step",
    staleBody:
      "This step was saved by someone else while you were editing. Load their version, then re-apply your changes.",
    staleReload: "Load the current version",
    staleSavedAt: "Saved {{time}}",
    busyTitle: "Setup is busy",
    busyBody: "Another setup change is running. Try again in a moment.",
    busyRetry: "Try again",
    alreadyLiveTitle: "Your factory is already live",
    alreadyLiveBody:
      "Set-up is kept as a record now. Make changes on the screens where you do the work.",
  },

  preconditions: {
    title: "Finish these first",
    body: "This step cannot be completed until the following are done:",
    stepNote: "Step {{number}} could not be completed.",
  },

  // Precondition keys the API returns in `unmet[]` (§3.1). Unknown keys fall
  // back to the raw key via i18next `defaultValue`, never a blank line.
  precondition: {
    vertical_pack_installed: "Install an industry pack",
    all_steps_completed: "Complete all four setup steps",
  },

  // ---- checklist (§3.6) — S8 owns the detail; the shell only summarises -----
  checklist: {
    title: "Go-live checklist",
    blurb: "Blocking checks must pass before you go live.",
    open: "Open the checklist",
    categories: {
      vertical: "Industry pack",
      quality: "Quality", // D-214
      catalogue: "Catalogue",
      plant: "Plant",
      people: "People",
      opening: "Opening state",
      channels: "Channels",
    },
    itemStatus: {
      pending: "Not checked",
      passed: "Passed",
      failed: "Not met",
      waived: "Waived",
    },
    blocking: "Blocking",

    // S8 owns the rest of this block: item titles, the per-item rule strings the
    // evaluator names in `detail.rule_key`, the owning-module labels, evidence
    // field labels and the waive / attest / go-live copy.
    ...checklistScreenEn,
  },

  // ---- shared modal (components/wizard-dialog.tsx) -------------------------
  dialog: {
    cancel: "Cancel",
  },

  // ---- S7 · step 4 · opening state (§3.5) ----------------------------------
  opening: {
    intro: "Where the factory stands on day one: {{openingStock}}, customers and open orders.",
    scopeTitle: "What you can load today",
    scopeBody:
      "Load each lane from a spreadsheet. Customers and open orders go straight into sales.",
    asOfLabel: "Balances are as of",
    asOfHint:
      "The business date your opening figures were taken on. Saved with this step; it does not post anything on its own.",
    tabs: {
      label: "Opening state",
      stock: "Stock",
      commercial: "Customers and suppliers",
      orders: "Open orders",
    },
  },

  screens: {
    home: "Set up your factory",
    vertical: "Choose your industry pack",
    catalogue: "Load your catalogue",
    importDetail: "Import",
    plant: "Map your plant",
    checklist: "Go-live checklist",
    terminology: "Terminology",
  },

  // ---- shared request states (§4: every screen has all three) --------------
  state: {
    loading: "Loading…",
    errorTitle: "Something went wrong",
    errorDetail: "The request could not be completed. Try again.",
    retry: "Retry",
    requestId: "Request ID",
    empty: "Nothing here yet.",
    notPermitted: "You do not have access to this.",
  },

  // ---- base labels for the 61 canonical term keys (§2.5) -------------------
  // ONLY a fallback: `useTerm()` prefers the effective label from
  // GET /v1/terminology (tenant → pack → base) and uses these while that call
  // is in flight or has failed, so a raw key never reaches a user.
  terms: {
    work_order: "Work order",
    work_center: "Work center",
    routing_step: "Routing step",
    stock_unit: "Stock unit",
    lot: "Lot",
    remnant: "Remnant",
    off_cut: "Off-cut",
    waste: "Waste",
    scrap: "Scrap",
    rework: "Rework",
    shift: "Shift",
    department: "Department",
    facility: "Facility",
    machine: "Machine",
    operator: "Operator",
    quote: "Quote",
    sales_order: "Sales order",
    purchase_order: "Purchase order",
    supplier: "Supplier",
    customer: "Customer",
    bom: "Bill of materials",
    product: "Product",
    material: "Material",
    opening_stock: "Opening stock",
    pull_test: "Pull test",
    quality_check: "Quality check",
    downtime: "Downtime",
    setup: "Setup",
    teardown: "Teardown",
    barrel: "Barrel",
    bobbin: "Bobbin",
    batch: "Batch",
    planned_order: "Planned order",
    promise_date: "Promise date",
    drawing: "Drawing",
    twisting: "Twisting",
    coating: "Coating",
    remaining_length: "Remaining length",
    measured_length: "Measured length",
    claimed_length: "Claimed length",
    calibration_variance: "Calibration variance",
    location: "Location",
    warehouse: "Warehouse",
    transfer: "Transfer",
    cycle_count: "Cycle count",
    defect: "Defect",
    hold: "Hold",
    release: "Release",
    co_product: "Co-product",
    by_product: "By-product",
    regrind: "Regrind",
    pellet: "Pellet",
    extrusion: "Extrusion",
    armoring: "Armoring",
    sheathing: "Sheathing",
    engraving: "Engraving",
    // "Cross section" — no hyphen, matching TERMINOLOGY_BASE in
    // packages/contracts. This block is the fallback shown while
    // GET /v1/terminology is in flight; when the two disagree the label
    // visibly changes on every load. Pinned by tools/ci/i18n-parity.ts check 5.
    cross_section: "Cross section",
    core_count: "Core count",
    voltage_rating: "Voltage rating",
    color_code: "Color code",
    price_tier: "Price tier",
  },
} as const;

// Shape with values widened to `string` (src/i18n-shape.ts) — `typeof` alone
// carries the string-LITERAL types out of the `as const`, which no translation
// can satisfy. Keys are still parity-checked at compile time.
export type OnboardingDict = Translated<typeof onboardingEn>;

// ru is TRANSLATED (onboarding.ru.ts). fr, zh, zh-Hant and hi are
// machine-translated in onboarding.<locale>.ts, which src/locales/<locale>.ts
// imports directly. The claims are declared once in src/i18n-completeness.ts
// and enforced by tools/ci/i18n-parity.ts.
export const onboardingRu: OnboardingDict = onboardingRuDict;
